import {ActionItemDto, MIP_SOURCE} from '@domain/pom'
import {Tag} from '@hconnect/common/components'
import {DateFormat, GridSetting} from '@hconnect/uikit'
import {Grid} from '@mui/material'
import {EventStatusLabel} from '@widgets/common-shift-event'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

import {TaskDescription} from './TaskDescription'

const regularSize: GridSetting = {
  xs: 12,
  sm: 4,
  md: 6
}

type Props = {
  action: ActionItemDto
  timezone: string
}

// the element only belongs to MIP actions, and only once one has been chosen
const hasElement = ({source, element}: ActionItemDto): boolean =>
  source === MIP_SOURCE && Boolean(element)

const MipElementTag = ({action}: {action: ActionItemDto}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const translate = (key: string) => t(key)

  if (!hasElement(action)) return null

  return (
    <Grid item xs={12}>
      <Tag label={translate('pomAction.label.element')} data-test-id="task-details-mip-element">
        {translate(`pomAction.mipElement.${action.element}`)}
      </Tag>
    </Grid>
  )
}

export const PlantActionDetails = ({action, timezone}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const translate = (key: string) => t(key)
  // actions created before the cost leadership flag existed are group cost benchmarking ones
  const isCostLeadership = action.costLeadership ?? action.source === 'groupCostBenchmarking'

  return (
    <>
      <Grid container spacing={2}>
        <Grid item {...regularSize}>
          <Tag label={translate('task.label.status')} data-test-id="task-details-status">
            <EventStatusLabel status={action.status} />
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag label={translate('task.label.dueDate')} data-test-id="task-details-dueDate">
            <DateFormat date={action.dueDate} timezone={timezone} />
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag
            label={translate('pomAction.label.source')}
            data-test-id="task-details-action-source"
          >
            {translate(`pomAction.actionSource.${action.source}`)}
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag
            label={translate('pomAction.label.costLeadership')}
            data-test-id="task-details-cost-leadership"
          >
            {translate(`pomAction.costLeadershipValues.${isCostLeadership ? 'yes' : 'no'}`)}
          </Tag>
        </Grid>
        <MipElementTag action={action} />
        {isCostLeadership && (
          <>
            <Grid item xs={12}>
              <Tag
                label={translate('pomAction.label.kpiName')}
                data-test-id="task-details-kpi-name"
              >
                {action.kpiName ? translate(`pomAction.kpiName.${action.kpiName}`) : '-'}
              </Tag>
            </Grid>
            {(action.targetCosts || action.actualCostsYtd) && (
              <Grid item xs={12} container spacing={2}>
                <Grid item xs={6}>
                  <Tag
                    label={translate('pomAction.label.targetCosts')}
                    data-test-id="task-details-target-costs"
                  >
                    {action.targetCosts ?? '-'}
                  </Tag>
                </Grid>
                <Grid item xs={6}>
                  <Tag
                    label={translate('pomAction.label.actualCostsYtd')}
                    data-test-id="task-details-actual-costs-ytd"
                  >
                    {action.actualCostsYtd ?? '-'}
                  </Tag>
                </Grid>
              </Grid>
            )}
            {(action.targetKpi || action.actualKpiYtd) && (
              <Grid item xs={12} container spacing={2}>
                <Grid item xs={6}>
                  <Tag
                    label={translate('pomAction.label.targetKpi')}
                    data-test-id="task-details-target-kpi"
                  >
                    {action.targetKpi ?? '-'}
                  </Tag>
                </Grid>
                <Grid item xs={6}>
                  <Tag
                    label={translate('pomAction.label.actualKpiYtd')}
                    data-test-id="task-details-actual-kpi-ytd"
                  >
                    {action.actualKpiYtd ?? '-'}
                  </Tag>
                </Grid>
              </Grid>
            )}
          </>
        )}
        {action.questionId && (
          <Grid item {...regularSize}>
            <Tag
              label={translate('pomAction.label.connectedTo')}
              data-test-id="task-details-connectedTo"
            >
              {action.questionId}
            </Tag>
          </Grid>
        )}
        <Grid item {...regularSize}>
          <Tag label={translate('task.label.assignee')} data-test-id="task-details-assignee">
            {action.assignee?.name ?? '-'}
          </Tag>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tag
            label={translate('task.label.description')}
            descriptionSx={{fontWeight: 400, fontSize: 14}}
            data-test-id="task-details-description"
          >
            <TaskDescription description={action.description} />
          </Tag>
        </Grid>
      </Grid>
    </>
  )
}
